import React, { Fragment, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { CmsEntities } from 'client/data/models/cms';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { AnimatedHoverLinkButton } from 'site-modules/editorial/components/top-rated/hover-button/animated-hover-link-button';
import { getFullImageUrl } from 'client/utils/image-helpers';
import { ElectricBolt } from 'site-modules/editorial/components/top-rated/icons/electric-bolt';

import './finalists.scss';

export function Finalists({ entry, titleLabel = 'FINALIST', isMobile = false, isElectricPage = false }) {
  const ref = useRef(null);
  const entries = entry.children();
  const isOneEntry = entries.length === 1;
  const styles = index => ({
    'pe-md-2 ps-md-1_5': index % 2 === 0 && !isOneEntry,
    'ps-md-2 pe-md-1_5': index % 2 === 1 && !isOneEntry,
    'w-100 px-md-1_5': isOneEntry,
  });
  const marginXStyles = index => ({
    'me-md-0_5 me-lg-1_25': index % 2 === 0 && !isOneEntry,
    'ms-md-0_5 ms-lg-1_25': index % 2 === 1 && !isOneEntry,
  });

  const headerToShow = () => {
    const title = entry.metadata('title').value();
    const subtitle = entry.metadata('subtitle').value();
    const split = title.split(' ');
    const firstString = split.slice(0, split.length - 2).join(' ');
    const restString = split.slice(split.length - 2).join(' ');

    return (
      <header className="cool-gray-100 size-30 fw-bold pt-3 mt-2 pb-2 text-center">
        <span className="text-orange-60 fw-medium mb-0_5 text-shadow-contrast d-block">
          <span className="hidden-md-down size-24">{firstString}</span>
          <span className="hidden-md-up size-20">{firstString}</span>
        </span>
        <span className="fw-bold text-white text-shadow-contrast d-block">
          <span className="hidden-md-down size-48 ">{restString}</span>
          <span className="hidden-md-up size-30">{restString}</span>
        </span>
        {subtitle && <span className="text-cool-gray-60 fw-normal mt-0_5 mt-md-1 size-20 d-block">{subtitle}</span>}
      </header>
    );
  };

  const renderCardElementLink = finalist => (
    <a
      className="card-element-link pos-a top-0 left-0 right-0 bottom-0"
      href={finalist.metadata('buttonShopLink').value()}
    >
      <span className="visually-hidden ">{finalist.metadata('buttonShopText').value()}</span>
    </a>
  );

  useEffect(() => {
    const refCurrent = ref.current;
    entries.forEach((finalist, index) => {
      refCurrent.querySelectorAll(`.index-${index + 1}`).forEach(el => {
        el.addEventListener('mouseenter', () => {
          refCurrent.querySelector(`.gallery-${index + 1}`).classList.add('on-hover');
        });
        el.addEventListener('mouseleave', () => {
          refCurrent.querySelector(`.gallery-${index + 1}`).classList.remove('on-hover');
        });
      });
    });
  }, []);

  return (
    <article className="finalists-wrapper mx-auto pos-r">
      {headerToShow()}
      <section
        className={classnames('finalists', {
          'two-column': !isOneEntry,
        })}
        ref={ref}
      >
        {entries.map((finalist, index) => {
          const title = finalist.metadata('title').value();
          const links = finalist.links();
          const mappedLinks = links.map(link => ({ href: link.href, title: link.captionTranscript }));
          return (
            <Fragment key={`fragment-${title}`}>
              <figure
                className={classnames(`gallery-${index + 1} index-${index + 1}`, marginXStyles(index), {
                  'w-100 mx-auto': isOneEntry,
                  'mt-3 mt-md-0': index > 0,
                })}
                style={{ marginBottom: '-80px', minHeight: '348px' }}
                data-testid={`gallery-${index + 1}`}
              >
                <img
                  src={getFullImageUrl(mappedLinks[0].href, 700)}
                  alt={mappedLinks[0].title || ''}
                  className="w-100 h-100"
                  loading="lazy"
                  data-index={index + 1}
                  height={348}
                  width="100%"
                  style={{ objectFit: 'cover', minHeight: '348px' }}
                />
                {isElectricPage && <ElectricBolt size={32} wrapperClass="pos-a top-0 right-0 mt-2 me-2" />}
                {renderCardElementLink(finalist)}
              </figure>
              <header
                className={classnames(
                  `vehicle-heading-${index + 1} index-${index + 1} px-md-1 pos-r`,
                  styles(index),
                  marginXStyles(index)
                )}
                style={{ verticalAlign: 'bottom' }}
              >
                <span
                  className={classnames('size-14 fw-bold mb-0_25 label-finalist text-shadow-contrast', {
                    'text-blue-50': !isElectricPage,
                    'electric-text': isElectricPage,
                  })}
                >
                  {titleLabel}
                </span>
                <h2 className="cool-gray-100 size-30 mb-1_25 vehicle-name text-shadow-contrast">{title}</h2>
                {renderCardElementLink(finalist)}
              </header>
              <article
                className={classnames(
                  `vehicle-content-${index + 1} index-hover-only-${index + 1} pos-r`,
                  styles(index),
                  marginXStyles(index)
                )}
                style={{ verticalAlign: 'top' }}
              >
                <hr className="m-0" />
                <ContentFragment classes="cool-gray-100 mt-1_5">{finalist.metadata('content').value()}</ContentFragment>
              </article>
              <footer
                className={classnames(
                  `core-link-${index + 1} index-${index + 1} d-md-flex align-items-center justify-content-between`,
                  styles(index),
                  marginXStyles(index)
                )}
              >
                <AnimatedHoverLinkButton
                  linkUrl={finalist.metadata('buttonShopLink').value()}
                  linkText={finalist.metadata('buttonShopText').value()}
                  wrapperClass="my-0_5"
                  isPreHovered={isMobile}
                  buttonTextColor="text-white"
                />
                <a
                  className="float-md-end mt-1_5 mt-md-0 d-block text-core-link"
                  href={finalist.metadata('coreLink').value()}
                >
                  <span className="text-underline text-blue-50 text-capitalize">
                    {finalist.metadata('coreLinkText').value()}
                  </span>
                </a>
              </footer>
            </Fragment>
          );
        })}
      </section>
    </article>
  );
}

Finalists.propTypes = {
  entry: CmsEntities.Content.isRequired,
  titleLabel: PropTypes.string,
  isMobile: PropTypes.bool,
  isElectricPage: PropTypes.bool,
};
